// src/components/VisitasCard.tsx
//
// Cuántas personas entraron a la página pública, de dónde vinieron, y cuántas
// terminaron reservando.
//
// POR QUÉ LOS TRES NÚMEROS JUNTOS
// -------------------------------
// Las visitas solas no dicen nada. Un profesional con la agenda impecable y
// cero turnos tiene dos problemas posibles, opuestos entre sí:
//
//   pocas visitas   → nadie tiene el link. Hay que repartirlo.
//   muchas visitas y pocas reservas → la página o el precio frenan.
//
// Por eso la tarjeta muestra siempre visitas Y reservas, y dice en palabras
// qué conviene hacer. Un número sin lectura se mira una vez y no se vuelve.

import React, { useEffect, useState } from 'react';
import { Eye, Calendar, TrendingUp, Share2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Resumen {
  dias: number;
  visitas: number;
  reservas: number;
  conversion: number | null;
  porOrigen: { source: string; n: number }[];
}

const ETIQUETA_ORIGEN: Record<string, string> = {
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  google: 'Buscadores',
  twitter: 'X / Twitter',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  directo: 'Entraron directo',
  otro: 'Otros',
};

interface Props {
  /** Link público. Si no se pasa, se deduce de la sesión. */
  urlPublica?: string;
}

/**
 * El link de la página pública, sacado de la sesión.
 *
 * Se resuelve acá adentro y no se recibe siempre por prop para que la tarjeta
 * se pueda poner en cualquier pantalla sin tener que cablearle datos. Prefiere
 * el slug (consultarapp.com/p/ana-perez) sobre el id, que es el mismo criterio
 * que usa el botón de compartir.
 */
const linkDeLaSesion = (): string | undefined => {
  try {
    const crudo = localStorage.getItem('user');
    if (!crudo) return undefined;
    const u = JSON.parse(crudo);
    const identificador = u?.slug || u?.id;
    return identificador ? `${window.location.origin}/p/${identificador}` : undefined;
  } catch {
    return undefined;
  }
};

const VisitasCard: React.FC<Props> = ({ urlPublica: urlProp }) => {
  const urlPublica = urlProp || linkDeLaSesion();
  const [datos, setDatos] = useState<Resumen | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/professionals/visitas/resumen?dias=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (res.ok && json.success) setDatos(json);
      } catch {
        /* sin métricas se muestra vacío, no un error: no es información crítica */
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, []);

  if (cargando) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center justify-center text-gray-500 text-left">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-[14px]">Cargando visitas…</span>
      </div>
    );
  }

  if (!datos) return null;

  const { visitas, reservas, conversion, porOrigen } = datos;

  // La lectura en palabras. Es lo que convierte el número en una decisión.
  const consejo = (() => {
    if (visitas === 0) {
      return {
        tono: 'bg-[#FF9500]/10 border-[#FF9500]/40 text-[#7A3D00]',
        texto: 'Todavía no entró nadie a tu página. El link es lo único que falta: ponelo en la bio de Instagram y pasáselo por WhatsApp a tus pacientes.',
      };
    }
    if (reservas === 0) {
      return {
        tono: 'bg-[#0FA3B1]/10 border-[#0FA3B1]/40 text-[#0B5C66]',
        texto: `Entraron ${visitas} ${visitas === 1 ? 'persona' : 'personas'} pero nadie reservó todavía. Si se repite, puede que convenga revisar los precios o la seña.`,
      };
    }
    return {
      tono: 'bg-[#34C759]/10 border-[#34C759]/40 text-[#1B5E20]',
      texto: `De cada 100 personas que entran, ${conversion} sacan turno. Tu página está convirtiendo.`,
    };
  })();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 text-left">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 text-[17px]">Tu página pública</h3>
        <span className="text-[12px] text-gray-500">últimos 30 días</span>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <Eye className="w-5 h-5 text-[#0FA3B1] mx-auto mb-1" />
          <div className="text-[26px] font-bold text-gray-900 leading-none">{visitas}</div>
          <div className="text-[12px] text-gray-600 mt-1">visitas</div>
        </div>
        <div className="text-center">
          <Calendar className="w-5 h-5 text-[#0FA3B1] mx-auto mb-1" />
          <div className="text-[26px] font-bold text-gray-900 leading-none">{reservas}</div>
          <div className="text-[12px] text-gray-600 mt-1">turnos</div>
        </div>
        <div className="text-center">
          <TrendingUp className="w-5 h-5 text-[#0FA3B1] mx-auto mb-1" />
          <div className="text-[26px] font-bold text-gray-900 leading-none">
            {conversion === null ? '—' : `${conversion}%`}
          </div>
          <div className="text-[12px] text-gray-600 mt-1">reservan</div>
        </div>
      </div>

      {porOrigen.length > 0 && (
        <div className="mb-4">
          <div className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
            De dónde vienen
          </div>
          <div className="space-y-1.5">
            {porOrigen.slice(0, 4).map((o) => (
              <div key={o.source} className="flex items-center justify-between text-[14px]">
                <span className="text-gray-700">{ETIQUETA_ORIGEN[o.source] || o.source}</span>
                <span className="font-semibold text-gray-900">{o.n}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`p-3 rounded-xl border text-[13px] leading-snug ${consejo.tono}`}>
        {consejo.texto}
      </div>

      {urlPublica && visitas === 0 && (
        <button
          onClick={() => {
            if (navigator.share) {
              navigator.share({ title: 'Mi página', url: urlPublica }).catch(() => {});
            } else {
              navigator.clipboard?.writeText(urlPublica);
            }
          }}
          className="mt-3 w-full flex items-center justify-center gap-2 bg-[#0FA3B1] text-white px-4 py-3 rounded-xl font-semibold text-[15px] active:scale-[0.98] active:bg-[#0D8F9C] transition-all"
        >
          <Share2 className="w-4 h-4" />
          Compartir mi página
        </button>
      )}
    </div>
  );
};

export default VisitasCard;
